/*******************************************************************************
 *
 * AWS CDK CLI Option Merge
 *
 * Generic helper that folds a precedence-ordered list of `CdkXxxOptions`
 * fragments into a single resolved option set. Used by every
 * `CdkCli.*OptionsFor(target)` accessor to assemble the documented 5-level
 * override stack (per-stage default → per-account override → per-target
 * override → consumer override → call-site explicit) without per-command merge
 * logic.
 *
 * Merge semantics (see also the `CdkCli` JSDoc):
 *
 * - **Scalars** (booleans, strings, numbers, enums): a non-`undefined` value in
 *   a later (higher-precedence) layer overwrites the previous value. An
 *   `undefined` value in a later layer is treated as "no opinion" and falls
 *   through to whatever lower layers contributed.
 * - **Arrays**: concatenated low → high precedence, then deduplicated preserving
 *   document order (first occurrence wins). This lets a per-stage default seed
 *   a list and per-target/per-account layers append to it without losing the
 *   seeded entries.
 * - **Records / maps** (plain object values, including `tags` and `context`
 *   when modelled as `Record<string, string>`): merged one level deep; later
 *   layers win on key collisions, earlier layers' keys survive otherwise.
 * - **`undefined` layers** and **`undefined` field values** short-circuit — no
 *   field on the output is touched by a missing layer or a `undefined` entry.
 *
 ******************************************************************************/

/**
 * Internal: returns true when `value` is a plain object that should be treated
 * as a record/map for merge purposes. Arrays and `null` are intentionally
 * excluded — they are scalar at the field level (arrays follow the array
 * concat-and-dedupe path; `null` is not part of the option surface).
 */
const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null && !Array.isArray(value);
};

/**
 * Dedupe an array while preserving first-occurrence order. Used after the
 * concat step so a per-stage default + per-target override that both name the
 * same entry collapse to a single entry.
 */
const dedupePreservingOrder = <T>(items: Array<T>): Array<T> => {
  const seen = new Set<T>();
  const out: Array<T> = [];
  for (const item of items) {
    if (!seen.has(item)) {
      seen.add(item);
      out.push(item);
    }
  }
  return out;
};

/**
 * Fold a precedence-ordered list of partial CDK CLI option layers into a
 * single resolved option set.
 *
 * Layers are supplied in **lowest-precedence-first** order. Each subsequent
 * layer's non-`undefined` values overwrite the previous accumulator for
 * scalars; arrays are concatenated and deduped; record-valued fields are
 * merged one level deep.
 *
 * `undefined` layers are skipped entirely. `undefined` field values within a
 * layer are skipped per-field — they never clobber a previously set value.
 *
 * @typeParam TOptions - The fully resolved option interface (e.g.
 *   `CdkDeployOptions`). The function returns `TOptions`, but the caller is
 *   responsible for ensuring every field its consumers depend on is supplied
 *   by at least one layer — the helper does not validate completeness.
 */
export const mergeCdkOptions = <TOptions>(
  ...layers: Array<Partial<TOptions> | undefined>
): TOptions => {
  const accumulator: Record<string, unknown> = {};

  for (const layer of layers) {
    if (layer === undefined) {
      continue;
    }

    for (const [key, incoming] of Object.entries(layer)) {
      if (incoming === undefined) {
        continue;
      }

      const existing = accumulator[key];

      if (Array.isArray(incoming)) {
        const previous = Array.isArray(existing) ? existing : [];
        accumulator[key] = dedupePreservingOrder([...previous, ...incoming]);
        continue;
      }

      if (isPlainObject(incoming)) {
        const previous = isPlainObject(existing) ? existing : {};
        accumulator[key] = { ...previous, ...incoming };
        continue;
      }

      // Scalar (boolean, string, number, enum) — override wins.
      accumulator[key] = incoming;
    }
  }

  return accumulator as TOptions;
};
